import { useMemo } from 'react';
import { isEqual } from 'lodash';

import type { AiAction } from '~/components/AiChat/types';
import { getAccentVariant } from '~/components/ClaimPage/CliveApp/CliveNotifications/accents';
import type { StpNotification } from '~/components/ClaimPage/CliveApp/CliveNotifications/types';

import type { ActionVersionsRead } from '../../ActionHistory/utils';
import { actionAsLogged, getSupersededEntries, isCurrentVersion } from '../../ActionHistory/utils';
import type { LogConversation } from '../../conversation/types';
import type { ConductorAsk } from '../../types';
import {
  getDrawnAskEntries,
  getNotifiedActionState,
  getNotifiedAskId,
  getReferencedActionId,
  isActionEntry,
  isAskEntryKind,
  isHandlerTurn,
  resolveActionState,
  resolveAskState,
} from '../../utils';
import type { LogEntryModel } from '../types';

import { buildConversation } from './buildConversation';

/**
 * Each notification's entry as last built. Keyed on the notification, which keeps its identity for as long as
 * its event is loaded, so an entry goes when its notification does.
 */
const entryByNotification = new WeakMap<StpNotification, LogEntryModel>();

/**
 * The entry as last built where it says the same thing, so a memoized entry skips the render. The row is
 * compared by value: every read of the rows builds each one anew.
 */
const keepUnchanged = (entry: LogEntryModel): LogEntryModel => {
  const kept = entryByNotification.get(entry.notification);
  if (kept && isEqual(kept, entry)) return kept;

  entryByNotification.set(entry.notification, entry);

  return entry;
};

/**
 * The stream with each ask drawn once, at the first entry about it — the raise, unless the window
 * starts after it. claims-ai narrates an ask's closure under the ask's own kind so that it updates
 * the card the raise drew rather than drawing a second one beside it, and the ask already says how
 * it closed. Folded on the id the entry carries, so it holds before the asks are read.
 */
const drawEachAskOnce = (notifications: StpNotification[]): StpNotification[] => {
  const drawn = getDrawnAskEntries(notifications);

  return notifications.filter((notification) => {
    const askId = getNotifiedAskId(notification);

    return !askId || drawn.get(askId) === notification;
  });
};

/**
 * The log's entries: one conductor notification joined to the action or the ask it is about, where
 * it names one.
 *
 * An entry that names neither is accented by its metadata, so an error the conductor published is
 * an orange card rather than a bare line.
 *
 * The sources answer different questions and only meet here: the stream says what happened and in
 * what order, the action rows and the asks say where each card has got to since. Keeping the join
 * in one place is what lets every entry component stay a component.
 *
 * The row says where an action is now, so only the last entry about it is joined to the row. One
 * before it was about a version Clive has since revised, and is joined to that version, off
 * `actionVersions`, where the history read holds it.
 *
 * Every snapshot and every read rebuilds the entries, and an entry that comes out unchanged is the
 * same object as before, so a live event draws only what it changed.
 *
 * Given a `conversation`, the entries are drawn as the log's conversation with Clive (`buildConversation`);
 * without one, as they always were.
 */
export const useLogEntries = (
  notifications: StpNotification[],
  actions: AiAction[],
  asks: ConductorAsk[],
  /** The history read for each action Clive revised, by action id. */
  actionVersions: Record<string, ActionVersionsRead>,
  conversation?: LogConversation
): LogEntryModel[] =>
  useMemo(() => {
    const actionsById = new Map(actions.map((action) => [action.action_id, action]));
    const rowFor = (notification: StpNotification): AiAction | undefined => {
      const actionId = getReferencedActionId(notification);

      return actionId ? actionsById.get(actionId) : undefined;
    };
    const superseded = getSupersededEntries(notifications, actions);
    const readFor = (notification: StpNotification): ActionVersionsRead | undefined => {
      const actionId = getReferencedActionId(notification);

      return actionId ? actionVersions[actionId] : undefined;
    };
    const versionFor = (notification: StpNotification): AiAction | undefined => {
      const row = rowFor(notification);
      const read = readFor(notification);

      return row && read ? actionAsLogged(row, read, notification.timestamp) : undefined;
    };
    const asksById = new Map(asks.map((ask) => [ask.id, ask]));
    const askFor = (notification: StpNotification): ConductorAsk | undefined => {
      const askId = getNotifiedAskId(notification);

      return askId ? asksById.get(askId) : undefined;
    };

    const toEntry = (notification: StpNotification): LogEntryModel => {
      if (isHandlerTurn(notification)) {
        // A turn before the action's last entry rejected a version that has since been revised.
        const action = superseded.has(notification) ? versionFor(notification) : rowFor(notification);

        return { kind: 'handler_turn', notification, action };
      }

      // Milestone or not, and with or without a row to join: an entry with no row (a failed call,
      // a declared approval no card was seen for) takes the state it published.
      if (isActionEntry(notification)) {
        const action = rowFor(notification);
        const supersededBy = superseded.get(notification);
        // Where the action is now is its last entry's to say: the history opened from an earlier card names it.
        const published = (supersededBy ?? notification).metadata?.action_state;
        const state = resolveActionState(action, getNotifiedActionState(published));

        if (!supersededBy) return { kind: 'action', notification, action, state };

        const read = readFor(notification);
        const version = versionFor(notification);
        const isCurrent =
          version !== undefined && !!read && isCurrentVersion(read, notification.timestamp, supersededBy.timestamp);

        return { kind: 'action', notification, action, state, superseded: { version, isCurrent } };
      }

      // Decided by `entry_kind`, as an action entry is, and resolved from the ask alone: the entry
      // is a snapshot from when it was published.
      const entryKind = notification.metadata?.entry_kind;
      if (isAskEntryKind(entryKind)) {
        const ask = askFor(notification);
        if (ask?.kind === 'request') return { kind: 'request', notification, ask, state: resolveAskState(ask) };
        if (ask?.kind === 'question') return { kind: 'question', notification, ask, state: resolveAskState(ask) };

        return { kind: entryKind, notification };
      }

      const accent = getAccentVariant(notification.metadata);

      return accent ? { kind: 'accent', notification, accent } : { kind: 'log', notification };
    };

    const entries = drawEachAskOnce(notifications).map(toEntry);

    return (conversation ? buildConversation(entries, conversation) : entries).map(keepUnchanged);
  }, [notifications, actions, asks, actionVersions, conversation]);
